import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import DocumentItem from "./DocumentItem";
import DocumentBreadcrumb from "./DocumentBreadcrumb";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import UploadImageBackground from "./UploadImageBackground";
import FilterDocuments from "./FilterDocuments";

import getFileExtension from "utils/getFileExtension";
import debounceFn from "utils/debounce";

import {
  useLazyGetDocumentsQuery,
  usePostUploadUserDocuments,
} from "apis/DocumentType";

import {
  Container,
  DropzoneContainer,
  FolderContainer,
  InputContainer,
  StyledInput,
} from "./style";

const acceptFiles = "image/*, .pdf, .doc, .docx, .PDF, .DOC, .DOCX";
const docFileFormats = ["doc", "docx"];
const validFileFormats = ["jpg", "png", "jpeg", "pdf", ...docFileFormats];

const Documents = ({ t, documentEnumType, title }) => {
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [searchedText, setSearchedText] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState("");
  const [query, setQuery] = useState({
    adminDocumentType: documentEnumType,
    search: "",
    path: "",
  });

  const [
    getDocuments,
    {
      data: { data: { documents: documentsData, previousPath } = {} } = {},
      isLoading,
      isFetching,
    },
  ] = useLazyGetDocumentsQuery();

  const [uploadDocument, { isLoading: isDocumentUploading }] =
    usePostUploadUserDocuments();

  const handleChange = (e) => {
    setSearchedText(e.target.value);
    debounceFn(() => {
      setQuery((prev) => ({ ...prev, search: e.target.value, previousPath }));
    }, 500);
  };

  const handleUploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append("document", selectedFile);
      formData.append("path", previousPath);
      formData.append("adminDocumentType", documentEnumType);
      await uploadDocument(formData);
      setDocumentToPreview(null);
    } catch (error) {
      console.error(error);
    }
  };

  const onDrop = (files) => {
    const fileExtension = getFileExtension(files[0]?.name).toLowerCase();
    if (!validFileFormats.includes(fileExtension)) {
      toast.error("Invalid file format, Please upload image, pdf or word file");
    } else if (docFileFormats.includes(fileExtension)) {
      handleUploadFile(files[0]);
    } else {
      setDocumentToPreview({
        url: URL.createObjectURL(files[0]),
        documentTypeName: files[0].name,
        documentType: fileExtension,
        documentName: files[0].name,
      });
    }
    setUploadedDocument(files[0]);
  };

  useEffect(() => {
    (async () => {
      try {
        await getDocuments(query);
      } catch (error) {}
    })();
  }, [documentsData, query]);

  return (
    <Container>
      <Backdrop open={isLoading || isFetching || isDocumentUploading} />
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          isDownloadable={false}
          actionButton={
            <Button
              onClick={() => handleUploadFile(uploadedDocument)}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isDocumentUploading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}
      {previousPath && (
        <DropzoneContainer>
          <Dropzone
            multiple={false}
            noClick
            onDrop={onDrop}
            accept={acceptFiles}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <InputContainer {...getRootProps()}>
                <StyledInput {...getInputProps()} />
                {isDragActive && <UploadImageBackground />}
              </InputContainer>
            )}
          </Dropzone>
        </DropzoneContainer>
      )}

      <FolderContainer>
        <Grid container className="w-100 m-0">
          <Grid item xs={12} className="p-0">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between m-0 mb-2 ps-2">
              <div className="d-flex gap-2">
                {previousPath && (
                  <IconButton
                    onClick={() => {
                      const paths = previousPath?.split("\\");
                      paths.pop();
                      setQuery((prev) => ({
                        ...prev,
                        path: paths?.join("\\") ?? "",
                        search: "",
                      }));
                      setSearchedText("");
                    }}
                    sx={{ height: "19px", width: "19px" }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <h4 className="mb-sm-0">{t(title)}</h4>
              </div>

              <div className="page-title-right">
                <DocumentBreadcrumb
                  path={previousPath ?? ""}
                  userName={
                    previousPath && documentsData?.[0]?.userName
                      ? documentsData[0].userName
                      : ""
                  }
                  setSearchedText={setSearchedText}
                  setQuery={setQuery}
                  documentEnumType={documentEnumType}
                />
              </div>
            </div>
          </Grid>
        </Grid>
        <FilterDocuments
          searchedText={searchedText}
          path={previousPath ?? ""}
          handleChange={handleChange}
          handleUploadFile={handleUploadFile}
          fileChangeHandler={onDrop}
          documentEnumType={documentEnumType}
        />
        {!!documentsData?.length && !(isFetching || isLoading) && (
          <div className="d-flex gap-3 flex-wrap">
            {documentsData.map((item, index) => (
              <DocumentItem
                document={item}
                setQuery={setQuery}
                key={index}
                documentEnumType={documentEnumType}
                setSearchedText={setSearchedText}
              />
            ))}
          </div>
        )}
        {!documentsData?.length && !(isFetching || isLoading) && (
          <Typography
            color="GrayText"
            textAlign="center"
            variant="body1"
            className="ms-1 mt-1"
          >
            {t("This folder is empty!")}
          </Typography>
        )}
      </FolderContainer>
    </Container>
  );
};

Documents.propTypes = {
  t: PropTypes.func,
  documentEnumType: PropTypes.number,
  title: PropTypes.string,
};

export default withTranslation()(Documents);
